import React, { useState, useEffect, Fragment, useRef } from 'react'
import styled from 'styled-components'
import { useTable, useGroupBy, useExpanded } from 'react-table'
import { useFilters, useGlobalFilter, useAsyncDebounce } from 'react-table'
import Select from '../../Relatorio1/Select'
import { matchSorter } from 'match-sorter'

const Styles = styled.div`
  padding: 1rem;

  table {
    border-spacing: 0;
    border: 1px solid black;

    tr {
      :last-child {
        td {
          border-bottom: 0;
        }
      }
    }

    th,
    td {
      margin: 0;
      padding: 0.5rem;
      border-bottom: 1px solid black;
      border-right: 1px solid black;

      :last-child {
        border-right: 0;
      }
    }
  }
`

// Defina uma UI padrão para filtragem
function DefaultColumnFilter({
  column: { filterValue, preFilteredRows, setFilter },
}) {
  const count = preFilteredRows.length

  return (
    <input
      value={filterValue || ''}
      onChange={e => {
        setFilter(e.target.value || undefined) // defina undefined para remover o filtro completamente
      }}
      placeholder={`${count} registros`}
    />
  )
}

function fuzzyTextFilterFn(rows, id, filterValue) {
  return matchSorter(rows, filterValue, { keys: [row => row.values[id]] })
}

// Deixe a tabela remover o filtro se a string estiver vazia
fuzzyTextFilterFn.autoRemove = val => !val

// Define uma função de filtro de filtro personalizado!
function filterGreaterThan(rows, id, filterValue) {
  return rows.filter(row => {
    const rowValue = row.values[id]
    return rowValue >= filterValue
  })
}

filterGreaterThan.autoRemove = val => typeof val !== 'number'

function Table({ columns, data }) {

  const filterTypes = React.useMemo(
    () => ({
      // Adicionar um novo tipo de filtro fuzzyTextFilterFn
      fuzzyText: fuzzyTextFilterFn,
      // Ou substituir o filtro de texto padrão para usar "startWith"
      text: (rows, id, filterValue) => {
        return rows.filter(row => {
          const rowValue = row.values[id]
          return rowValue !== undefined
            ? String(rowValue)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
            : true
        })
      },
    })
  )

  const defaultColumn = React.useMemo(
    () => ({
      // UI de filtro padrão
      Filter: DefaultColumnFilter,
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { groupBy, expanded },
    visibleColumns,
    preGlobalFilteredRows,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      defaultColumn,
      filterTypes,
    },
    useFilters,
    useGlobalFilter,
    useGroupBy,
    useExpanded,
  )

  const firstPageRows = rows.slice(0, 100)

  return (
    <Fragment>

      {/*
      <pre>
        <code>{JSON.stringify({ groupBy, expanded }, null, 2)}</code>
      </pre>
      */}

      <Legend />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps()}>
                  {column.canGroupBy ? (
                    // Se a coluna puder ser agrupada, adiciona um toggle
                    <span {...column.getGroupByToggleProps()}>
                      {column.isGrouped ? '🛑 ' : '👊 '}
                    </span>
                  ) : null}
                  {column.render('Header')}
                  {/* Render the columns filter UI */}
                  <div>{column.canFilter ? column.render('Filter') : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {firstPageRows.map((row, i) => {
            prepareRow(row)
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  return (
                    <td
                      // Colorir as celulas
                      {...cell.getCellProps()}
                      style={{
                        background: cell.isGrouped
                          ? '#0aff0082'
                          : cell.isAggregated
                          ? '#ffa50078'
                          : cell.isPlaceholder
                          ? '#ff000042'
                          : 'white',
                      }}
                    >
                      {cell.isGrouped ? (
                        // Se for uma célula agrupada, adicione um expansor e contagem de linhas
                        <Fragment>
                          <span {...row.getToggleRowExpandedProps()}>
                            {row.isExpanded ? '👇' : '👉'}
                          </span>{' '}
                          {cell.render('Cell')} ({row.subRows.length})
                        </Fragment>
                      ) : cell.isAggregated ? (
                        // Se a célula estiver agregada, use o agregador renderizador para célula
                        cell.render('Aggregated')
                      ) : cell.isPlaceholder ? null : ( // Para células com valores repetidos, renderizar nulo
                        // Caso contrário, apenas renderize a célula
                        cell.render('Cell')
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <br />
      <div>Mostrando os primeiros {rows.length} resultados</div>
    </Fragment>

  )
}

function Legend() {
  return (
    <div
      style={{
        padding: '0.5rem 0',
      }}
    >
      <span
        style={{
          display: 'inline-block',
          background: '#0aff0082',
          padding: '0.5rem',
        }}
      >
        Agrupado
      </span>{' '}
      <span
        style={{
          display: 'inline-block',
          background: '#ffa50078',
          padding: '0.5rem',
        }}
      >
        Agregado
      </span>{' '}
      <span
        style={{
          display: 'inline-block',
          background: '#ff000042',
          padding: '0.5rem',
        }}
      >
        Valor Repetido
      </span>
    </div>
  )
}

function Agregador(props) {

  let [ state, setState ] = useState();
  let [ url, setUrl ] = useState(props.url || `${process.env.REACT_APP_API_URL}/dadosgerais`);

  let cols = props.colunasSelecionadas ? props.colunasSelecionadas : []

  let columns = [
    {
      Header: 'Educação Profissional',
      columns: cols,
    }
  ]

  useEffect(() => {

    async function buscar() {
      let dados = await fetch(url).then(res => res.json()).then(res => res).catch(err => {});
      setState(dados.dados);
    }

    buscar();
  
  }, [url]);

  let data = React.useMemo(() => state, [state]);

  function selectMes(event) {
    setUrl(`${process.env.REACT_APP_API_URL}/dadosgerais?mes=${event.target.value}`)
  }

  return (
    <Styles>
      <Select selectMes={selectMes}/>
      { state && <Table columns={columns} data={data} /> }
    </Styles>
  )
}

export default Agregador;